import React, { useState, useCallback, useEffect } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import usePersistedFilters from "../hooks/usePersistedFilters";
import usePersistedPageSize from "../hooks/usePersistedPageSize";
import NewAddressFilters from "../components/NewAddress/NewAddressFilters";
import NewAddressTable from "../components/NewAddress/NewAddressTable";
import NewAddressButtons from "../components/NewAddress/NewAddressButtons";
import PaginationComponent from "../components/PaginationComponent";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import "../assets/styles/newAddress.scss";
import api from "../services/httpClient";

const NewAddressList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "newAddress",
    20,
  );
  const { filters, handleFilterChange, clearFilters, hasActiveFilters } =
    usePersistedFilters("newAddress");
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sortConfig, setSortConfig] = useState({
    key: "DateAdd",
    direction: "desc",
  });

  const [data, setData] = useState({
    rows: [],
    total: 0,
  });

  const fetchData = useCallback(
    async (
      pageNumber = currentPage,
      pageSize = itemsPerPage,
      filtersToApply = appliedFilters,
      sortConfigToApply = sortConfig,
    ) => {
      try {
        const offset = (pageNumber - 1) * pageSize;

        const requestBody = {
          sort: sortConfigToApply.key,
          order: sortConfigToApply.direction,
          offset: offset,
          limit: pageSize,
          search: {
            cityName: filtersToApply.cityName || "",
            postcode: filtersToApply.postcode || "",
            street: filtersToApply.street || "",
            status:
              filtersToApply.status !== undefined &&
              filtersToApply.status !== ""
                ? Number(filtersToApply.status)
                : null,
          },
        };

        const response = await api.post(`/NewAddress/List`, requestBody);

        const result = response.data?.data || response.data;
        const rows = Array.isArray(result.items)
          ? result.items
          : Array.isArray(result.rows)
            ? result.rows
            : [];
        const total = result.itemsCount ?? result.total ?? 0;
        setData({ rows, total });
      } catch (error) {
        setData({ rows: [], total: 0 });
      }
    },
    [currentPage, itemsPerPage, appliedFilters, sortConfig],
  );

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImmediateFilterChange = (field, value) => {
    handleFilterChange(field, value);
    const updatedFilters = { ...filters, [field]: value };
    setAppliedFilters(updatedFilters);
    fetchData(1, itemsPerPage, updatedFilters);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    fetchData(1, itemsPerPage, filters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    fetchData(currentPage, itemsPerPage, appliedFilters, newSortConfig);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / itemsPerPage));

  return (
    <div className="new-address-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="New Address Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Nowe adresy
            </h3>
          </Col>
          <Col xs="auto">
            <NewAddressButtons onSearch={handleSearch} />
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <NewAddressFilters
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onImmediateFilterChange={handleImmediateFilterChange}
              filters={filters}
            />
          </Col>
          <Col className="flex-grow-1" />
        </Row>
        <Row className="justify-content-end mb-3">
          <Col xs="auto">
            <Button
              variant={hasActiveFilters ? "success" : "outline-secondary"}
              size="sm"
              className="me-3"
              onClick={() => {
                clearFilters();
                setAppliedFilters({});
                fetchData(1, itemsPerPage, {});
                setCurrentPage(1);
              }}
            >
              Wyczyść filtry
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <NewAddressTable data={data.rows} onSortChange={handleSortChange} />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">

          <PaginationComponent
            itemsPerPage={itemsPerPage}
            dataCount={data.rows.length}
            itemsCount={data.total}
            totalCount={data.total}
            pageNumber={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              setCurrentPage(page);
              fetchData(page, itemsPerPage);
            }}
            onItemsPerPageChange={(pageSize) => {
              const size = Number(pageSize);
              setItemsPerPage(size);
              setCurrentPage(1);
              fetchData(1, size);
            }}
            itemsPerPageOptions={[20, 100, 500]}
          />
            
        </Row>
      </Container>
    </div>
  );
};

export default NewAddressList;
